import { getDistance2D } from 'cd-eft/components/SurveyEngineering/Maps/SETMapFunctions/elevationInterpolationEngine';
import { XyzPoint } from 'cd-eft/models/SurveyEngineering/XyzPoint';
import { action } from 'mobx';
import { DynamicAlignmentHub } from './DynamicAlignmentHub';
import { DynamicTableStoreBase } from '../DynamicTableStoreBase';
import { ProfileKeyCode } from '../DynamicProfileEnums';
import { calcTangentCircle } from 'cd-eft/utils/planeGeometryUtils';
import { DynamicAlignmentMapStore } from './DynamicAlignmentMapStore';
import stores from 'cd-eft/models/Stores';

const { alignmentDesignStore } = stores.AppStore;

class DynamicAlignmentTableStore extends DynamicTableStoreBase {
  public hub: DynamicAlignmentHub;
  protected _highlitedRows: string[] = [];
  private _initialAlignmentStation;



  /**
   *
   */
  constructor(hub: DynamicAlignmentHub) {
    super();
    this.hub = hub;
    this.profileKeys = this.hub._isTerraceAlignment ? [ProfileKeyCode.TGS, ProfileKeyCode.REACH] : [ProfileKeyCode.IA];
  }

  public get isAlignmentValid(): boolean {
    return this.hub.isAlignmentValid;
  }

  // adapt to global stores
  set setStore(setStore) {
    this.hub.setStore = setStore;
  }

  get setStore() {
    return this.hub.setStore;
  }

  public set alignmentStore(store) {
    this.hub.alignmentStore = store;
  }

  public get alignmentStore() {
    return this.hub.alignmentStore;
  }

  private get initialAlignmentStation() {
      if (this.hub.alignmentStore.begginingStation !== undefined) {
          this.setStore.initialAlignmentStation.stationName = Number(this.hub.alignmentStore.begginingStation);
      }
      if (!this._initialAlignmentStation) {
          this._initialAlignmentStation = this.setStore.initialAlignmentStation;
      }
      return this._initialAlignmentStation;
  }

  /** private methods */
  @action
  async addRow(dataPoint: XyzPoint, eventState: string, vertexIndex: number) {
    const stations = this.currentObject.alignmentStations;
    const newRow = { ...this.initialAlignmentStation };
    newRow.x = dataPoint.getX().toFixed(this.precision);
    newRow.y = dataPoint.getY().toFixed(this.precision);
    newRow.z = dataPoint.getZ().toFixed(this.precision);

    if (eventState === 'start') {
      this.currentObject.beginningX = newRow.x;
      this.currentObject.beginningY = newRow.y;
    }

    if (vertexIndex <= stations.length - 1) {
      stations[vertexIndex] = newRow;
    } else {
      stations.push({ ...newRow });
      vertexIndex = stations.length - 1;
    }

    if (vertexIndex > 0) {
      const p1 = new XyzPoint(stations[vertexIndex - 1].x, stations[vertexIndex - 1].y, 0);
      const p2 = new XyzPoint(stations[vertexIndex].x, stations[vertexIndex].y, 0);
      const dist = +stations[vertexIndex - 1].stationDistance + getDistance2D(p1, p2);
      stations[vertexIndex].stationDistance = dist.toFixed(this.precision);
      stations[vertexIndex].stationName = Math.round(dist) + Number(this.hub.alignmentStore.begginingStation); 
    } 
  }

  @action
  protected async insertRow(dataPoint: XyzPoint, vertexIndex: number) {
    if (vertexIndex < 0){return};
    const newRow = { ...this.initialAlignmentStation };
    newRow.x = dataPoint.getX().toFixed(this.precision);
    newRow.y = dataPoint.getY().toFixed(this.precision);
    newRow.z = dataPoint.getZ().toFixed(this.precision);

    const stations = this.currentObject.alignmentStations;
    stations.splice(vertexIndex, 0, newRow);

    if (vertexIndex === 0) {
      this.calculateStationDistances();
    } else {
      const p1 = new XyzPoint(stations[vertexIndex - 1].x, stations[vertexIndex - 1].y, 0);
      const p2 = new XyzPoint(stations[vertexIndex].x, stations[vertexIndex].y, 0);
      const dist = +stations[vertexIndex - 1].stationDistance + getDistance2D(p1, p2);
      stations[vertexIndex].stationDistance = dist.toFixed(this.precision);
      stations[vertexIndex].stationName = Math.round(dist);
    }
    alignmentDesignStore.disableSaveAlignment = false;
  }

  @action updateAlignment(data: any, index) {
    this.hub?.updateAlignmentOnMap(data, index);
  }

  @action onCurveValueChanged(index: number, curveValue: number, curveType: 'radius' | 'none' = 'radius') {
    if (curveType === 'radius' && curveValue > 0) {
      const circleProps = calcTangentCircle(this.currentObject.alignmentStations, index, curveValue);
      (this.hub.MapStore as DynamicAlignmentMapStore).drawCurveCircle(circleProps.centerX, circleProps.centerY, curveValue);
    }
  }

  clearCurveCircle() {
    (this.hub.MapStore as DynamicAlignmentMapStore).clearCurveCircle();
  }

  public reset() {
    if (this.currentObject) {
      this.currentObject.alignmentStations.length = 0;
      const newRow = { ...this.initialAlignmentStation };
      this.currentObject.alignmentStations.push(newRow);

      this.currentObject.alignmentEntryMethod = 'Coordinates';
    }
  }

  protected calculateStationDistances() {
    const beginningStation = Number(this.alignmentStore.begginingStation) > 0 ? Number(this.alignmentStore.begginingStation) : 0;
    const stations = this.currentObject.alignmentStations;
    let p1 = new XyzPoint(stations[0].x, stations[0].y, 0);
    stations[0].stationDistance = beginningStation;
    stations[0].stationName = beginningStation;
    for (let index = 1; index < stations.length; index++) {
      const p2 = new XyzPoint(stations[index].x, stations[index].y, 0);
      const dist = +stations[index - 1].stationDistance + getDistance2D(p1, p2);
      stations[index].stationDistance = dist.toFixed(this.precision);
      stations[index].stationName = Math.round(dist);

      p1 = p2;
    }
  }

  protected addRows() { }
  protected insertRows(info: any, vertexIndex: number) { }

  protected modifyRows(info: any, vertexIndex: number) { }

  deleteStationFromMapClick(deleteFunction) {
    this.hub?.deleteStationFromMapClick(deleteFunction);
  }

  clearDeleteStation() {
    this.hub?.clearDeleteStation();
  }

  public highlightRows(i: number) {
    const id = 'key-alignmentStation-' + i;
    if (!this._highlitedRows.some(r => r === id)) {
      this.tryHighlight(id);
      this._highlitedRows.push(id);
    }
  }

  public clearHighlights() {
    for (const row of this._highlitedRows) {
      this.tryHighlight(row, false);
    }
    this._highlitedRows = [];
  }

  protected tryHighlight(id: string, highlight: boolean = true) {
    const element = document.getElementById(id);
    if (element) {
      if (highlight) {
        element.classList.add("highlighted-row");
      } else {
        element.classList.remove("highlighted-row");
      }
    }
  }
}

export { DynamicAlignmentTableStore };
